import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import Select from "../components/SelectScenarios";
// Ajusta si tu API devuelve otro shape
const DIET_FIELDS = [
  "CEREALS",
  "FISH",
  "FRUVEG",
  "PORK",
  "MILK",
  "OLSOIL",
  "EGGS",
  "PULSES",
  "REDMEAT",
  "ROOTS",
  "SUGAR",
  "POULTRY",
  "NUTS",
  "ANIMFAT",
  "BEVSPICES",
  "OTHER",
  "ALCOHOL",
];

const IMPLEMENTATION_TIMING = [
  { id: 1, name: "Linear" },
  { id: 2, name: "Delayed" },
  { id: 3, name: "Fast" },
  { id: 4, name: "Other" },
  { id: 5, name: "AfforTiming" },
];

export default function ScenariosPage({
  t = (k, o) => o?.defaultValue ?? k,
  Sidebar = null,
  FloatChat = null,
}) {
  const {
    authFetch,
    bootstrapping,
    pendingTask,
    pendingTaskState,
    startPendingTask,
    user,
    selectedScenathon,
  } = useAuth();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  // ===== data backend (como jsonData de EJS) =====
  const [jsonData, setJsonData] = useState(null);
  const [customScenarios, setCustomScenarios] = useState([]); // ✅ DB scenarios
  const [country, setCountry] = useState(
    String(user?.country).trim().toUpperCase(),
  );

  // ===== impl timing =====
  const [implTiming, setImplTiming] = useState([]);
  const implOptions = implTiming.map((x) => x?.name).filter(Boolean);

  // ===== tabs =====
  const [tab, setTab] = useState(() => {
    const saved = parseInt(localStorage.getItem("scenarios_last_tab"), 10);
    return Number.isInteger(saved) && saved >= 1 && saved <= 5 ? saved : 1;
  });

  useEffect(() => {
    localStorage.setItem("scenarios_last_tab", String(tab));
  }, [tab]);

  // ===== UI =====
  const [loadingPage, setLoadingPage] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ==========================
  // Helpers: normalizaciones
  // ==========================
  function normalizeString(x) {
    return String(x ?? "").trim();
  }

  function ensureObjectConfig(config) {
    if (!config) return null;
    if (typeof config === "object") return config;
    if (typeof config === "string") {
      try {
        const parsed = JSON.parse(config);
        return parsed && typeof parsed === "object" ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  }

  function extractProductsFromConfig(config) {
    const cfg = ensureObjectConfig(config);
    if (!cfg) return [];
    if (Array.isArray(cfg.products)) return cfg.products;
    // fallback por si viene anidado
    if (cfg.Imports && Array.isArray(cfg.Imports.products)) return cfg.Imports.products;
    if (cfg.Exports && Array.isArray(cfg.Exports.products)) return cfg.Exports.products;
    return [];
  }

  // ==========================
  // Refresh (re-cargar jsonData + customScenarios)
  // ==========================
  const refreshDefined = useCallback(async () => {
    const res = await authFetchRef.current("/api/pathways/definedscenarios", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

    const location = String(data?.location || country).trim().toUpperCase();
    setCountry(location);
    const payload = data?.jsonData?.[location] ?? null;
    setJsonData(normalizeScenarioData(payload));

    // ✅ depende de tu backend modificado
    setCustomScenarios(Array.isArray(data?.customScenarios) ? data.customScenarios : []);
  }, [country]);

  // ==========================
  // 1) LOAD jsonData + customScenarios
  // ==========================
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    async function load() {
      try {
        setLoadingPage(true);
        setError("");
        if (bootstrapping) return;

        const res = await authFetchRef.current("/api/pathways/definedscenarios", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);

        const location = String(data?.location || country).trim().toUpperCase();
        const payload = data?.jsonData?.[location] ?? null;
        const normalized = normalizeScenarioData(payload);

        if (!alive) return;
        setCountry(location);
        setJsonData(normalized);
        setCustomScenarios(Array.isArray(data?.customScenarios) ? data.customScenarios : []);
      } catch (e) {
        if (e?.name !== "AbortError") setError(e?.message || "Unexpected error");
      } finally {
        if (alive && !bootstrapping) setLoadingPage(false);
      }
    }

    load();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [bootstrapping]);

  // ==========================
  // 2) LOAD impltiming (local)
  // ==========================
  useEffect(() => {
    setImplTiming(Array.isArray(IMPLEMENTATION_TIMING) ? IMPLEMENTATION_TIMING : []);
  }, []);

  // ==========================
  // Names por tipo (desde DB)
  // ==========================
  const namesByType = useMemo(() => {
    const out = {
      Diet: [],
      Imports: [],
      Exports: [],
      ProtectedAreas: [],
      Afforestation: [],
    };

    for (const s of customScenarios || []) {
      const type = String(s.type_scenario || "");
      const name = String(s.name || "").trim();
      if (!name) continue;
      if (out[type]) out[type].push(name);
    }

    for (const k of Object.keys(out)) {
      out[k] = Array.from(new Set(out[k])).sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [customScenarios]);

  const dietNamesCustom = namesByType.Diet;
  const importNamesCustom = namesByType.Imports;
  const exportNamesCustom = namesByType.Exports;
  const paNamesCustom = namesByType.ProtectedAreas;
  const affNamesCustom = namesByType.Afforestation;

  // ==========================
  // DIET TAB STATE
  // ==========================
  const [dietScenarioName, setDietScenarioName] = useState(
    () => localStorage.getItem("diet_selected") || "",
  );
  const [dietImplTiming, setDietImplTiming] = useState("");
  const [expandedDietField, setExpandedDietField] = useState("");
  const [dietValues, setDietValues] = useState(() => {
    const init = {};
    DIET_FIELDS.forEach((k) => (init[k] = 0));
    return init;
  });

  const dietTotal = useMemo(() => {
    return DIET_FIELDS.reduce(
      (sum, k) => sum + (parseInt(dietValues[k] || 0, 10) || 0),
      0,
    );
  }, [dietValues]);

  // nombres base (del jsonData) + nombres custom (DB)
  const dietNames = useMemo(() => {
    const base = jsonData?.Diets ? Object.keys(jsonData.Diets) : [];
    return Array.from(new Set([...(base || []), ...(dietNamesCustom || [])])).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [jsonData, dietNamesCustom]);

  // Cargar dieta desde jsonData si existe
  useEffect(() => {
    localStorage.setItem("diet_selected", dietScenarioName);
    if (!jsonData?.Diets) return;

    const dieta = jsonData.Diets[dietScenarioName];
    if (!dieta) return;

    setDietValues((prev) => {
      const next = { ...prev };
      DIET_FIELDS.forEach((k) => {
        next[k] = dieta?.[k] ?? 0;
      });
      return next;
    });
  }, [dietScenarioName, jsonData]);

  // ✅ Cargar dieta desde DB si existe (tiene prioridad si coincide)
  useEffect(() => {
    const name = normalizeString(dietScenarioName);
    if (!name) return;

    const row = (customScenarios || []).find(
      (s) => String(s.type_scenario) === "Diet" && String(s.name) === name,
    );
    if (!row?.config) return;

    const cfg = ensureObjectConfig(row.config);
    if (!cfg) return;

    const diet = cfg?.Diet || cfg;
    const timing = diet?.implementationTiming;
    if (typeof timing === "string" && timing) setDietImplTiming(timing);

    // esperamos que venga { [name]: {CEREALS..} } o directo {CEREALS..}
    const valuesObj = diet?.[name] && typeof diet?.[name] === "object" ? diet[name] : diet;

    setDietValues((prev) => {
      const next = { ...prev };
      DIET_FIELDS.forEach((k) => {
        next[k] = valuesObj?.[k] ?? 0;
      });
      return next;
    });
  }, [dietScenarioName, customScenarios]);

  useEffect(() => {
    if (!dietImplTiming && implOptions.length) setDietImplTiming(implOptions[0]);
  }, [implOptions, dietImplTiming]);

  // ==========================
  // IMPORTS TAB STATE
  // ==========================
  const [importName, setImportName] = useState("");
  const [importImplTiming, setImportImplTiming] = useState("");
  const [importItems, setImportItems] = useState([]); // [{id, product, share2050}]
  const impIdRef = useRef(0);

  useEffect(() => {
    if (!importImplTiming && implOptions.length) setImportImplTiming(implOptions[0]);
  }, [implOptions, importImplTiming]);

  const initialImportsSorted = useMemo(() => {
    const arr = jsonData?.initial_imports || [];
    return [...arr].sort((a, b) => String(a.Product).localeCompare(String(b.Product)));
  }, [jsonData]);

  const selectedImportProducts = useMemo(
    () => new Set(importItems.map((x) => x.product).filter(Boolean)),
    [importItems],
  );

  const availableImportProducts = useMemo(() => {
    return initialImportsSorted
      .map((x) => x.Product)
      .filter((p) => p && !selectedImportProducts.has(p));
  }, [initialImportsSorted, selectedImportProducts]);

  const addImportItem = () => {
    setImportItems((prev) => [...prev, { id: ++impIdRef.current, product: "", share2050: "" }]);
  };

  const removeImportItem = (id) => {
    setImportItems((prev) => prev.filter((x) => x.id !== id));
  };

  const updateImportItem = (id, patch) => {
    setImportItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  function getImportMatch(product) {
    return initialImportsSorted.find((x) => x.Product === product) || null;
  }

  // ✅ Autoload Imports desde DB
  useEffect(() => {
    const name = normalizeString(importName);
    if (!name) return;

    const row = (customScenarios || []).find(
      (s) => String(s.type_scenario) === "Imports" && String(s.name) === name,
    );
    if (!row?.config) return;

    const cfg = ensureObjectConfig(row.config);
    if (!cfg) return;

    const timing = cfg?.implementationTiming;
    if (typeof timing === "string" && timing) setImportImplTiming(timing);

    const products = extractProductsFromConfig(cfg);
    setImportItems(
      (products || []).map((p, idx) => ({
        id: idx + 1,
        product: p?.Product || "",
        share2050:
          p?.ShareImport2050 !== undefined && p?.ShareImport2050 !== null
            ? String(Number(p.ShareImport2050) * 100)
            : "",
      })),
    );
    impIdRef.current = (products || []).length;
  }, [importName, customScenarios]);

  // ==========================
  // EXPORTS TAB STATE
  // ==========================
  const [exportName, setExportName] = useState("");
  const [exportImplTiming, setExportImplTiming] = useState("");
  const [exportItems, setExportItems] = useState([]); // [{id, product, netExport2050}]
  const expIdRef = useRef(0);

  useEffect(() => {
    if (!exportImplTiming && implOptions.length) setExportImplTiming(implOptions[0]);
  }, [implOptions, exportImplTiming]);

  const initialExportsSorted = useMemo(() => {
    const arr = jsonData?.initial_exports || [];
    return [...arr].sort((a, b) => String(a.Product).localeCompare(String(b.Product)));
  }, [jsonData]);

  const selectedExportProducts = useMemo(
    () => new Set(exportItems.map((x) => x.product).filter(Boolean)),
    [exportItems],
  );

  const availableExportProducts = useMemo(() => {
    return initialExportsSorted
      .map((x) => x.Product)
      .filter((p) => p && !selectedExportProducts.has(p));
  }, [initialExportsSorted, selectedExportProducts]);

  const addExportItem = () => {
    setExportItems((prev) => [...prev, { id: ++expIdRef.current, product: "", netExport2050: "" }]);
  };

  const removeExportItem = (id) => {
    setExportItems((prev) => prev.filter((x) => x.id !== id));
  };

  const updateExportItem = (id, patch) => {
    setExportItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  function getExportMatch(product) {
    return initialExportsSorted.find((x) => x.Product === product) || null;
  }

  // ✅ Autoload Exports desde DB
  useEffect(() => {
    const name = normalizeString(exportName);
    if (!name) return;

    const row = (customScenarios || []).find(
      (s) => String(s.type_scenario) === "Exports" && String(s.name) === name,
    );
    if (!row?.config) return;

    const cfg = ensureObjectConfig(row.config);
    if (!cfg) return;

    const timing = cfg?.implementationTiming;
    if (typeof timing === "string" && timing) setExportImplTiming(timing);

    const products = extractProductsFromConfig(cfg);
    setExportItems(
      (products || []).map((p, idx) => ({
        id: idx + 1,
        product: p?.Product || "",
        netExport2050:
          p?.netExport2050 !== undefined && p?.netExport2050 !== null
            ? String(p.netExport2050)
            : "",
      })),
    );
    expIdRef.current = (products || []).length;
  }, [exportName, customScenarios]);

  // ==========================
  // PROTECTED AREAS TAB STATE
  // ==========================
  const [paName, setPaName] = useState("");
  const [paMinShare, setPaMinShare] = useState("");
  const [paTargetShare, setPaTargetShare] = useState("");
  const [paImplTiming, setPaImplTiming] = useState("");

  useEffect(() => {
    if (!paImplTiming && implOptions.length) setPaImplTiming(implOptions[0]);
  }, [implOptions, paImplTiming]);

  // ✅ Autoload ProtectedAreas desde DB
  useEffect(() => {
    const name = normalizeString(paName);
    if (!name) return;

    const row = (customScenarios || []).find(
      (s) => String(s.type_scenario) === "ProtectedAreas" && String(s.name) === name,
    );
    if (!row?.config) return;

    const cfg = ensureObjectConfig(row.config);
    if (!cfg) return;

    const timing = cfg?.implementationTiming;
    if (typeof timing === "string" && timing) setPaImplTiming(timing);

    if (cfg?.minShare !== undefined && cfg?.minShare !== null) {
      setPaMinShare(String(Number(cfg.minShare) * 100));
    }
    if (cfg?.targetShare !== undefined && cfg?.targetShare !== null) {
      setPaTargetShare(String(Number(cfg.targetShare) * 100));
    }
  }, [paName, customScenarios]);

  // ==========================
  // AFFORESTATION TAB STATE
  // ==========================
  const [affName, setAffName] = useState("");
  const [affTarget, setAffTarget] = useState("");
  const [affImplTiming, setAffImplTiming] = useState("");

  useEffect(() => {
    if (!affImplTiming && implOptions.length) setAffImplTiming(implOptions[0]);
  }, [implOptions, affImplTiming]);

  // ✅ Autoload Afforestation desde DB
  useEffect(() => {
    const name = normalizeString(affName);
    if (!name) return;

    const row = (customScenarios || []).find(
      (s) => String(s.type_scenario) === "Afforestation" && String(s.name) === name,
    );
    if (!row?.config) return;

    const cfg = ensureObjectConfig(row.config);
    if (!cfg) return;

    const timing = cfg?.implementationTiming;
    if (typeof timing === "string" && timing) setAffImplTiming(timing);

    const target = cfg?.minShare ?? cfg?.target ?? cfg?.targetArea ?? null;
    if (target !== null && target !== undefined) setAffTarget(String(target));
  }, [affName, customScenarios]);

  // ==========================
  // overlay pointer-events
  // ==========================
  // =========================================================
  // ✅ Helpers: exists + tasktodo flow
  // =========================================================
  const checkScenarioExists = useCallback(
    async ({ name, type_scenario, country }) => {
      const params = new URLSearchParams({ name, type_scenario, country });
      const res = await authFetch(`/api/scenarios/exists?${params.toString()}`, {
        method: "GET",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);
      return !!data?.exists;
    },
    [authFetch],
  );

  const confirmReplaceIfExists = async ({ exists, name }) => {
    if (!exists) return { overwrite: false };

    const choice = await Swal.fire({
      icon: "warning",
      title: "Scenario already exists",
      text: `A scenario named "${name}" already exists. Do you want to replace it or change the name?`,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Replace",
      denyButtonText: "Change name",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
    });

    if (choice.isDenied) return { cancelled: true };
    if (!choice.isConfirmed) return { cancelled: true };
    return { overwrite: true };
  };

  const confirmSend = async () => {
    const c = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to submit your data?\nClick Confirm to continue or Cancel to review.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
    });
    return c.isConfirmed;
  };

  const getTaskIdByScenario = (scenarioType) => {
    const map = {
      Diet: 1,
      Imports: 2,
      Exports: 3,
      ProtectedAreas: 5,
      Afforestation: 6,
    };

    return map[scenarioType] ?? 0;
  };

  const saveScenarioViaTasktodo = useCallback(
    async ({ name, colname, colvalue, task_id }) => {
      const payload = {
        ...colvalue,
        ...(selectedScenathon?.id ? { scenathonId: selectedScenathon.id } : {}),
      };

      const res = await authFetch("/api/sendtask/tasktodo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id,
          name,
          status: "todo",
          onpc: 1,
          colname,
          colvalue: payload,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Error creating task (${res.status})`);
      const id = data?.id;
      if (!id) throw new Error("Backend did not return task id.");
      return { id, ok: true };
    },
    [authFetch],
  );

  // =========================================================
  // ✅ SUBMITS (Diet / Imports / Exports / PA / Afforestation)
  // =========================================================
  const submitDiet = async () => {
    try {
      setError("");

      const name = dietScenarioName.trim();
      if (!name) {
        await Swal.fire({ icon: "warning", title: "Warning!", text: "Scenario Name is empty" });
        return;
      }
      if (!dietImplTiming) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Implementation Timing is empty",
        });
        return;
      }

      const dietData2 = {};
      DIET_FIELDS.forEach((k) => (dietData2[k] = parseInt(dietValues[k] || 0, 10) || 0));

      const colvalue = {
        Diet: {
          Name: name,
          implementationTiming: dietImplTiming,
          [name]: dietData2,
        },
      };

      const exists = await checkScenarioExists({ name, type_scenario: "Diet", country });
      const r = await confirmReplaceIfExists({ exists, name });
      if (r.cancelled) return;

      const ok = await confirmSend();
      if (!ok) return;

      setIsSubmitting(true);
      const resp = await saveScenarioViaTasktodo({
        name,
        colname: "Diet",
        colvalue,
        overwrite: r.overwrite,
        task_id: getTaskIdByScenario("Diet"),
      });
      startPendingTask({ id: resp.id, name, country, colname: "Diet", overwrite: r.overwrite });
      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your scenario is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      if (err?.code === "SCENARIO_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Scenario already exists",
          text: "This scenario already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      console.error(err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitImports = async () => {
    try {
      setError("");

      const name = importName.trim();
      if (!name) {
        await Swal.fire({ icon: "warning", title: "Warning!", text: "Scenario Name is empty" });
        return;
      }
      if (!importImplTiming) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Implementation Timing is empty",
        });
        return;
      }

      const productos = [];
      for (const item of importItems) {
        if (!item.product || item.share2050 === "" || item.share2050 === null) continue;
        const match = getImportMatch(item.product);
        if (!match) continue;

        productos.push({
          Product: item.product,
          NetImport: parseFloat(match.NetImport),
          ShareImport2020: parseFloat(match.ShareImport),
          ShareImport2050: parseFloat(item.share2050) / 100,
        });
      }

      if (!productos.length) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Please add at least one product with valid data.",
        });
        return;
      }

      const colvalue = {
        scenarioName: name,
        implementationTiming: importImplTiming,
        products: productos,
      };

      const exists = await checkScenarioExists({ name, type_scenario: "Imports", country });
      const r = await confirmReplaceIfExists({ exists, name });
      if (r.cancelled) return;

      const ok = await confirmSend();
      if (!ok) return;

      setIsSubmitting(true);
      const resp = await saveScenarioViaTasktodo({
        name,
        colname: "PorcentajeImportado",
        colvalue,
        overwrite: r.overwrite,
        task_id: getTaskIdByScenario("Imports"),
      });
      startPendingTask({ id: resp.id, name, country, colname: "PorcentajeImportado", overwrite: r.overwrite });
      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your scenario is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      if (err?.code === "SCENARIO_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Scenario already exists",
          text: "This scenario already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      console.error(err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitExports = async () => {
    try {
      setError("");

      const name = exportName.trim();
      if (!name) {
        await Swal.fire({ icon: "warning", title: "Warning!", text: "Scenario Name is empty" });
        return;
      }
      if (!exportImplTiming) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Implementation Timing is empty",
        });
        return;
      }

      const productos = [];
      for (const item of exportItems) {
        if (!item.product || item.netExport2050 === "" || item.netExport2050 === null) continue;
        const match = getExportMatch(item.product);
        if (!match) continue;

        productos.push({
          Product: item.product,
          netExport: parseFloat(match.NetExport),
          netExport2050: parseFloat(item.netExport2050),
        });
      }

      if (!productos.length) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Please add at least one product with valid data.",
        });
        return;
      }

      const colvalue = {
        scenarioName: name,
        implementationTiming: exportImplTiming,
        products: productos,
      };

      const exists = await checkScenarioExists({ name, type_scenario: "Exports", country });
      const r = await confirmReplaceIfExists({ exists, name });
      if (r.cancelled) return;

      const ok = await confirmSend();
      if (!ok) return;

      setIsSubmitting(true);
      const resp = await saveScenarioViaTasktodo({
        name,
        colname: "EvolucionExportaciones",
        colvalue,
        overwrite: r.overwrite,
        task_id: getTaskIdByScenario("Exports"),
      });
      startPendingTask({ id: resp.id, name, country, colname: "EvolucionExportaciones", overwrite: r.overwrite });
      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your scenario is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      if (err?.code === "SCENARIO_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Scenario already exists",
          text: "This scenario already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      console.error(err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitPA = async () => {
    try {
      setError("");

      const name = paName.trim();
      const minShare = clampNum(paMinShare, 0, 100);
      const targetShare = clampNum(paTargetShare, 0, 100);

      if (!name || minShare === null || targetShare === null || !paImplTiming) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Please, complete all fields",
        });
        return;
      }

      const colvalue = {
        scenarioName: name,
        minShare: minShare / 100,
        targetShare: targetShare / 100,
        implementationTiming: paImplTiming,
      };

      const exists = await checkScenarioExists({
        name,
        type_scenario: "ProtectedAreas",
        country,
      });
      const r = await confirmReplaceIfExists({ exists, name });
      if (r.cancelled) return;

      const ok = await confirmSend();
      if (!ok) return;

      setIsSubmitting(true);
      const resp = await saveScenarioViaTasktodo({
        name,
        colname: "ExpansionAreasProtegidas",
        colvalue,
        overwrite: r.overwrite,
        task_id: getTaskIdByScenario("ProtectedAreas"),
      });
      startPendingTask({ id: resp.id, name, country, colname: "ExpansionAreasProtegidas", overwrite: r.overwrite });
      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your scenario is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      if (err?.code === "SCENARIO_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Scenario already exists",
          text: "This scenario already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      console.error(err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAfforestation = async () => {
    try {
      setError("");

      const name = affName.trim();
      const target = clampNum(affTarget, 0, 100000000);

      if (!name || target === null || !affImplTiming) {
        await Swal.fire({
          icon: "warning",
          title: "Warning!",
          text: "Please, complete all fields",
        });
        return;
      }

      const colvalue = {
        scenarioName: name,
        minShare: target,
        implementationTiming: affImplTiming,
      };

      const exists = await checkScenarioExists({ name, type_scenario: "Afforestation", country });
      const r = await confirmReplaceIfExists({ exists, name });
      if (r.cancelled) return;

      const ok = await confirmSend();
      if (!ok) return;

      setIsSubmitting(true);
      const resp = await saveScenarioViaTasktodo({
        name,
        colname: "Forestacion",
        colvalue,
        overwrite: r.overwrite,
        task_id: getTaskIdByScenario("Afforestation"),
      });
      startPendingTask({ id: resp.id, name, country, colname: "Forestacion", overwrite: r.overwrite });
      await Swal.fire({
        icon: "info",
        title: "Task sent",
        text: "Your scenario is being processed in background. You can navigate or refresh safely.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      if (err?.code === "SCENARIO_EXISTS") {
        await Swal.fire({
          icon: "warning",
          title: "Scenario already exists",
          text: "This scenario already exists. Change the name or choose to replace it.",
          confirmButtonColor: "#3085d6",
        });
        return;
      }
      console.error(err);
      setError(err?.message || "Error sending data");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================================================
  // Escucha eventos globales de tarea completada / fallida
  // =========================================================
  useEffect(() => {
    const onCompleted = async (event) => {
      const taskMeta = event?.detail?.pendingTask || {};
      if (taskMeta?.colname === "Pathway") return;
      try { await refreshDefined(); } catch { /* silent */ }
      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Scenario processed and saved successfully.",
        confirmButtonColor: "#3085d6",
      });
    };
    const onFailed = async (event) => {
      const detail = event?.detail || {};
      const taskMeta = detail?.pendingTask || {};
      if (taskMeta?.colname === "Pathway") return;
      const errMessage = detail?.error || "Error processing scenario task";
      setError(errMessage);
      await Swal.fire({
        icon: "error",
        title: "Task failed",
        text: errMessage,
        confirmButtonColor: "#3085d6",
      });
    };
    window.addEventListener("scenathon-task-completed", onCompleted);
    window.addEventListener("scenathon-task-failed", onFailed);
    return () => {
      window.removeEventListener("scenathon-task-completed", onCompleted);
      window.removeEventListener("scenathon-task-failed", onFailed);
    };
  }, [refreshDefined]);

  const isType2User = Number(user?.type_id) === 2 || Number(user?.type_id) === 4;
  const sendDisabled = isType2User || isSubmitting || !!pendingTask?.id;
  const hasScenarioPending = !!pendingTask?.id && pendingTask?.colname !== "Pathway";
  const showSendLoading = isSubmitting || hasScenarioPending;
  const scenarioBusyLabel = hasScenarioPending
     ? "Processing Scenario..."
    : "Implementing Scenario...";

  // ==========================
  // render helpers
  // ==========================
  const TabButton = ({ n, children }) => (
    <button
      type="button"
      onClick={() => setTab(n)}
      className={[
        "px-4 py-2 text-sm font-semibold rounded-xl transition",
        tab === n ? "bg-[#03203d] text-white" : "bg-white text-slate-700 hover:bg-slate-100",
      ].join(" ")}
    >
      {children}
    </button>
  );

  return (
    <div className="mt-10 w-full">
      {Sidebar ? <Sidebar /> : null}

      <main className="mx-auto w-full">
        {hasScenarioPending && (
          <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            {`Scenario task #${pendingTask.id} is processing (${pendingTaskState || "pending"}). You can keep working while it finishes.`}
            <div className="mt-3 flex w-full max-w-sm items-center gap-1" aria-hidden="true">
              <span className="h-1 flex-1 animate-pulse rounded-full bg-blue-300" />
              <span
                className="h-1 flex-1 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "0.15s" }}
              />
              <span
                className="h-1 flex-1 animate-pulse rounded-full bg-blue-500"
                style={{ animationDelay: "0.3s" }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Tabs header */}
        <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm ring-1 ring-slate-200">
          <TabButton n={1}>{t("scenario.tabs.diet", { defaultValue: "Diet" })}</TabButton>
          <TabButton n={2}>{t("scenario.tabs.imports", { defaultValue: "Imports" })}</TabButton>
          <TabButton n={3}>{t("scenario.tabs.exports", { defaultValue: "Exports" })}</TabButton>
          <TabButton n={4}>
            {t("scenario.tabs.protected", { defaultValue: "Protected areas expansion" })}
          </TabButton>
          <TabButton n={5}>
            {t("scenario.tabs.afforestation", { defaultValue: "Afforestation" })}
          </TabButton>
        </div>

        {/* Content */}
        <div className="mt-4">
          {loadingPage ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : (
            <>
              {/* TAB 1: DIET */}
              {tab === 1 && (
                <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-900">
                      {t("scenario.diet.title", { defaultValue: "Diet Scenario" })}
                    </h2>
                    <div
                      className={
                        `inline-flex items-center gap-2 rounded-xl px-5 py-2 text-lg font-bold shadow-sm ` +
                        (dietTotal <= 1500 || dietTotal >= 3000
                          ? 'bg-red-50 border border-red-200 text-red-800'
                          : (dietTotal > 1500 && dietTotal <= 2000) || (dietTotal > 2500 && dietTotal < 3000)
                          ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                          : 'bg-green-50 border border-green-200 text-green-800')
                      }
                    >
                      <span>{t("scenario.diet.total", { defaultValue: "Total (Kcal/cap/day):" })}</span>
                      <span className={dietTotal <= 1500 || dietTotal >= 3000 ? "text-2xl font-extrabold text-red-700" : (dietTotal > 1500 && dietTotal <= 2000) || (dietTotal > 2500 && dietTotal < 3000) ? "text-2xl font-extrabold text-yellow-700" : "text-2xl font-extrabold text-green-700"}>{dietTotal}</span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label={t("scenario.labels.name", { defaultValue: "NAME" })}>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={dietScenarioName}
                        onChange={(e) => setDietScenarioName(e.target.value)}
                        placeholder={t("scenario.placeholders.enterName", { defaultValue: "Enter Name" })}
                        list="datalist-diets"
                      />
                      <datalist id="datalist-diets">
                        {dietNames.map((d) => (
                          <option key={d} value={d} />
                        ))}
                      </datalist>
                    </Field>
                    <Field label={t("scenario.labels.implTiming", { defaultValue: "Implementation Timing" })}>
                      <Select
                        value={dietImplTiming}
                        onChange={setDietImplTiming}
                        options={implOptions}
                        placeholder="Select implementation timing"
                      />
                    </Field>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {DIET_FIELDS.map((field) => (
                      <div key={field} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-medium text-slate-800">{field}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={0}
                              max={2000}
                              className="w-20 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-slate-400"
                              value={dietValues[field] ?? 0}
                              onChange={(e) =>
                                setDietValues((prev) => ({
                                  ...prev,
                                  [field]: clampNum(e.target.value, 0, 2000) ?? 0,
                                }))
                              }
                            />

                            <button
                              type="button"
                              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                              onClick={() =>
                                setExpandedDietField((prev) => (prev === field ? "" : field))
                              }
                            >
                              {expandedDietField === field ? "Hide" : "Modify"}
                            </button>
                          </div>
                        </div>

                        <div
                          className={`overflow-hidden transition-all duration-200 ${
                            expandedDietField === field ? "mt-2 max-h-16" : "max-h-0"
                          }`}
                        >
                          {expandedDietField === field && (
                            <div className="flex items-center">
                              <input
                                type="range"
                                min={0}
                                max={2000}
                                step={1}
                                className="w-full accent-slate-900"
                                value={dietValues[field] ?? 0}
                                onChange={(e) =>
                                  setDietValues((prev) => ({
                                    ...prev,
                                    [field]: parseInt(e.target.value, 10) || 0,
                                  }))
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 text-center">
                    <PrimaryButton onClick={submitDiet} disabled={sendDisabled} showLoading={showSendLoading}>
                      {showSendLoading ? scenarioBusyLabel : t("scenario.send", { defaultValue: "Send Data" })}
                    </PrimaryButton>
                  </div>
                </section>
              )}

              {/* TAB 2: IMPORTS */}
              {tab === 2 && (
                <section className="space-y-4">
                  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                    <h2 className="text-lg font-bold text-slate-900">
                      {t("scenario.imports.title", { defaultValue: "Imports Scenario" })}
                    </h2>

                    <h3 className="mt-4 text-sm font-semibold text-slate-700">
                      {t("scenario.imports.mainProducts2020", {
                        defaultValue: "Main imported products in 2020",
                      })}
                    </h3>

                    <SimpleTable
                      headers={["", ...(jsonData?.topimports || []).map((_, i) => String(i + 1))]}
                      rows={[
                        [
                          t("scenario.labels.product", { defaultValue: "Product" }),
                          ...(jsonData?.topimports || []).map((x) => x.Product),
                        ],
                        [
                          t("scenario.imports.netImports2020", {
                            defaultValue: "Net Imports 2020 (1000 tons)",
                          }),
                          ...(jsonData?.topimports || []).map((x) => String(x.NetImport)),
                        ],
                        [
                          t("scenario.imports.shareImports2020", {
                            defaultValue: "Share of Imports 2020 (%)",
                          }),
                          ...(jsonData?.topimports || []).map((x) =>
                            ((x.ShareImport || 0) * 100).toFixed(1),
                          ),
                        ],
                      ]}
                    />
                  </div>

                  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Field label={t("scenario.labels.scenarioName", { defaultValue: "Scenario Name" })}>
                        <input
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                          value={importName}
                          onChange={(e) => setImportName(e.target.value)}
                          placeholder={t("scenario.placeholders.enterName", { defaultValue: "Enter Name" })}
                          list="datalist-imports"
                        />
                        <datalist id="datalist-imports">
                          {importNamesCustom.map((n) => (
                            <option key={n} value={n} />
                          ))}
                        </datalist>
                      </Field>

                      <Field
                        label={t("scenario.labels.implTiming", { defaultValue: "Implementation Timing" })}
                      >
                        <Select
                          value={importImplTiming}
                          onChange={setImportImplTiming}
                          options={implOptions}
                          placeholder="Select implementation timing"
                        />
                      </Field>
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={addImportItem}
                        className="rounded-xl bg-slate-900 px-6 py-0.8 text-sm font-semibold text-white hover:bg-[#54B046]"
                      >
                        +
                      </button>

                      <h3 className="text-base font-bold text-slate-900">
                        {t("scenario.addProducts", { defaultValue: "Products" })}
                      </h3>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                      {importItems.map((item) => {
                        const match = getImportMatch(item.product);
                        const share2020Pct = match ? (match.ShareImport * 100).toFixed(1) : "";
                        const netImport2020 = match ? String(match.NetImport) : "";
                        const share2050Pct = item.share2050;

                        const expected2050 =
                          match && share2050Pct !== "" && match.ShareImport
                            ? (
                              (match.NetImport * (parseFloat(share2050Pct) / 100)) /
                              match.ShareImport
                            ).toFixed(0)
                            : "";

                        const allowed = [
                          ...(item.product ? [item.product] : []),
                          ...availableImportProducts,
                        ].filter(Boolean);

                        return (
                          <div key={item.id} className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
                            <div className="grid grid-cols-1 gap-3">
                              <Field label="Product:">
                                <Select
                                  value={item.product || ""}
                                  onChange={(val) => updateImportItem(item.id, { product: val })}
                                  options={allowed}
                                  placeholder="Select Product"
                                />
                              </Field>

                              <Field label="Net Imports 2020:">
                                <input
                                  className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none"
                                  value={netImport2020}
                                  readOnly
                                />
                              </Field>

                              <Field label="Share of Imports 2020 (%):">
                                <input
                                  className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none"
                                  value={share2020Pct}
                                  readOnly
                                />
                              </Field>

                              <Field label="Share of Imports 2050 (%):">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
                                  value={share2050Pct}
                                  onChange={(e) =>
                                    updateImportItem(item.id, {
                                      share2050: clampNum(e.target.value, 0, 100) ?? "",
                                    })
                                  }
                                  placeholder="Value"
                                />
                              </Field>

                              <Field label="Expected Imports 2050:">
                                <input
                                  className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none"
                                  value={expected2050}
                                  readOnly
                                />
                              </Field>

                              <button
                                type="button"
                                onClick={() => removeImportItem(item.id)}
                                className="mt-1 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-6 text-center">
                      <PrimaryButton onClick={submitImports} disabled={sendDisabled} showLoading={showSendLoading}>
                        {showSendLoading ? scenarioBusyLabel : t("scenario.send", { defaultValue: "Send Data" })}
                      </PrimaryButton>
                    </div>
                  </div>
                </section>
              )}

              {/* TAB 3: EXPORTS */}
              {tab === 3 && (
                <section className="space-y-4">
                  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                    <h2 className="text-lg font-bold text-slate-900">
                      {t("scenario.exports.title", { defaultValue: "Exports Scenario" })}
                    </h2>

                    <h3 className="mt-4 text-sm font-semibold text-slate-700">
                      {t("scenario.exports.mainProducts2020", {
                        defaultValue: "Main exported products in 2020",
                      })}
                    </h3>

                    <SimpleTable
                      headers={[
                        "",
                        ...(jsonData?.topexports || []).slice(0, 5).map((_, i) => String(i + 1)),
                      ]}
                      rows={[
                        [
                          t("scenario.labels.product", { defaultValue: "Product" }),
                          ...(jsonData?.topexports || []).slice(0, 5).map((x) => x.Product),
                        ],
                        [
                          t("scenario.exports.netExports2020", {
                            defaultValue: "Net Exports 2020 (1000 tons)",
                          }),
                          ...(jsonData?.topexports || []).slice(0, 5).map((x) =>
                            String((x.NetExport ?? 0).toFixed?.(0) ?? x.NetExport),
                          ),
                        ],
                      ]}
                    />
                  </div>

                  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Field label={t("scenario.labels.scenarioName", { defaultValue: "Scenario Name" })}>
                        <input
                          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                          value={exportName}
                          onChange={(e) => setExportName(e.target.value)}
                          placeholder={t("scenario.placeholders.enterName", { defaultValue: "Enter Name" })}
                          list="datalist-exports"
                        />
                        <datalist id="datalist-exports">
                          {exportNamesCustom.map((n) => (
                            <option key={n} value={n} />
                          ))}
                        </datalist>
                      </Field>

                      <Field
                        label={t("scenario.labels.implTiming", { defaultValue: "Implementation Timing" })}
                      >
                        <Select
                          value={exportImplTiming}
                          onChange={setExportImplTiming}
                          options={implOptions}
                          placeholder="Select implementation timing"
                        />
                      </Field>
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={addExportItem}
                        className="rounded-xl bg-slate-900 px-6 py-0.8 text-sm font-semibold text-white hover:bg-[#54B046]"
                      >
                        +
                      </button>

                      <h3 className="text-base font-bold text-slate-900">
                        {t("scenario.addProducts", { defaultValue: "Products" })}
                      </h3>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                      {exportItems.map((item) => {
                        const match = getExportMatch(item.product);
                        const netExport2020 = match
                          ? String((match.NetExport ?? 0).toFixed?.(1) ?? match.NetExport)
                          : "";

                        const allowed = [
                          ...(item.product ? [item.product] : []),
                          ...availableExportProducts,
                        ].filter(Boolean);

                        return (
                          <div key={item.id} className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
                            <div className="grid grid-cols-1 gap-3">
                              <Field label="Product:">
                                <Select
                                  value={item.product || ""}
                                  onChange={(val) => updateExportItem(item.id, { product: val })}
                                  options={allowed}
                                  placeholder="Select Product"
                                />
                              </Field>

                              <Field label="Net Exports 2020:">
                                <input
                                  className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm outline-none"
                                  value={netExport2020}
                                  readOnly
                                />
                              </Field>

                              <Field label="Net Exports 2050:">
                                <input
                                  type="number"
                                  min={0}
                                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                                  value={item.netExport2050}
                                  onChange={(e) =>
                                    updateExportItem(item.id, {
                                      netExport2050: clampNum(e.target.value, 0, 500000) ?? "",
                                    })
                                  }
                                  placeholder="Value"
                                />
                              </Field>

                              <button
                                type="button"
                                onClick={() => removeExportItem(item.id)}
                                className="mt-1 rounded-xl bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-6 text-center">
                      <PrimaryButton onClick={submitExports} disabled={sendDisabled} showLoading={showSendLoading}>
                        {showSendLoading ? scenarioBusyLabel : t("scenario.send", { defaultValue: "Send Data" })}
                      </PrimaryButton>
                    </div>
                  </div>
                </section>
              )}

              {/* TAB 4: PROTECTED AREAS */}
              {tab === 4 && (
                <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                  <h2 className="text-lg font-bold text-slate-900">
                    {t("scenario.protected.title", {
                      defaultValue: "Protected Areas expansion Scenario",
                    })}
                  </h2>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                    <Field label={t("scenario.labels.scenarioName", { defaultValue: "Scenario Name" })}>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={paName}
                        onChange={(e) => setPaName(e.target.value)}
                        placeholder={t("scenario.placeholders.enterName", { defaultValue: "Enter Name" })}
                        list="datalist-pa"
                      />
                      <datalist id="datalist-pa">
                        {paNamesCustom.map((n) => (
                          <option key={n} value={n} />
                        ))}
                      </datalist>
                    </Field>

                    <Field
                      label={t("scenario.protected.minShareEcoregion", {
                        defaultValue: "Minimum share in every ecoregion (%)",
                      })}
                    >
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={paMinShare}
                        onChange={(e) => setPaMinShare(clampNum(e.target.value, 0, 100) ?? "")}
                        placeholder={t("scenario.placeholders.enterValue", { defaultValue: "Enter Value" })}
                      />
                    </Field>

                    <Field
                      label={t("scenario.protected.targetSharePA", {
                        defaultValue: "Target share of PA by 2050 (%)",
                      })}
                    >
                      <input
                        type="number"
                        min={0}
                        max={100}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={paTargetShare}
                        onChange={(e) => setPaTargetShare(clampNum(e.target.value, 0, 100) ?? "")}
                        placeholder={t("scenario.placeholders.enterValue", { defaultValue: "Enter Value" })}
                      />
                    </Field>

                    <Field
                      label={t("scenario.labels.implTiming", { defaultValue: "Implementation Timing" })}
                    >
                      <Select
                        value={paImplTiming}
                        onChange={setPaImplTiming}
                        options={implOptions}
                        placeholder="Select implementation timing"
                      />
                    </Field>
                  </div>

                  <div className="mt-6 text-center">
                    <PrimaryButton onClick={submitPA} disabled={sendDisabled} showLoading={showSendLoading}>
                      {showSendLoading ? scenarioBusyLabel : t("scenario.send", { defaultValue: "Send Data" })}
                    </PrimaryButton>
                  </div>
                </section>
              )}

              {/* TAB 5: AFFORESTATION */}
              {tab === 5 && (
                <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
                  <h2 className="text-lg font-bold text-slate-900">
                    {t("scenario.afforestation.title", { defaultValue: "Afforestation Scenario" })}
                  </h2>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label={t("scenario.labels.scenarioName", { defaultValue: "Scenario Name" })}>
                      <input
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={affName}
                        onChange={(e) => setAffName(e.target.value)}
                        placeholder={t("scenario.placeholders.enterName", { defaultValue: "Enter Name" })}
                        list="datalist-aff"
                      />
                      <datalist id="datalist-aff">
                        {affNamesCustom.map((n) => (
                          <option key={n} value={n} />
                        ))}
                      </datalist>
                    </Field>

                    <Field
                      label={t("scenario.afforestation.targetArea2050", {
                        defaultValue: "Target afforested area by 2050 (1000 ha)",
                      })}
                    >
                      <input
                        type="number"
                        min={0}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        value={affTarget}
                        onChange={(e) => setAffTarget(clampNum(e.target.value, 0, 100000000) ?? "")}
                        placeholder={t("scenario.placeholders.enterValue", { defaultValue: "Enter Value" })}
                      />
                    </Field>

                    <Field
                      label={t("scenario.labels.implTiming", { defaultValue: "Implementation Timing" })}
                    >
                      <Select
                        value={affImplTiming}
                        onChange={setAffImplTiming}
                        options={implOptions}
                        placeholder="Select implementation timing"
                      />
                    </Field>
                  </div>

                  <div className="mt-6 text-center">
                    <PrimaryButton onClick={submitAfforestation} disabled={sendDisabled} showLoading={showSendLoading}>
                      {showSendLoading ? scenarioBusyLabel : t("scenario.send", { defaultValue: "Send Data" })}
                    </PrimaryButton>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {FloatChat ? <FloatChat /> : null}
    </div>
  );
}

// ===== helpers =====
function normalizeScenarioData(payload) {
  const safe = payload || {};
  return {
    ...safe,
    topimports: Array.isArray(safe.topimports) ? safe.topimports : [],
    topexports: Array.isArray(safe.topexports) ? safe.topexports : [],
    initial_imports: Array.isArray(safe.initial_imports) ? safe.initial_imports : [],
    initial_exports: Array.isArray(safe.initial_exports) ? safe.initial_exports : [],
    Diets: safe.Diets && typeof safe.Diets === "object" ? safe.Diets : {},
  };
}

function clampNum(v, min, max) {
  if (v === "" || v === null || typeof v === "undefined") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.min(max, Math.max(min, n));
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled = false, showLoading = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-2xl px-6 py-3 text-lg font-semibold text-white shadow-lg transition ${disabled ? "cursor-not-allowed bg-slate-400" : "bg-[#03203d] hover:bg-[#54B046]"}`}
    >
      {showLoading ? (
        <span className="flex flex-col items-center gap-2 leading-none">
          <span className="inline-flex items-center gap-2">
            <svg
              className="h-4 w-4 animate-spin text-white"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" />
              <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <span>{children}</span>
          </span>
          <BusyProgressBar />
        </span>
      ) : (
        children
      )}
    </button>
  );
}

function BusyProgressBar() {
  return (
    <span className="flex w-40 items-center gap-1" aria-hidden="true">
      <span className="h-1 flex-1 animate-pulse rounded-full bg-white/35" />
      <span
        className="h-1 flex-1 animate-pulse rounded-full bg-white/55"
        style={{ animationDelay: "0.15s" }}
      />
      <span
        className="h-1 flex-1 animate-pulse rounded-full bg-white/75"
        style={{ animationDelay: "0.3s" }}
      />
    </span>
  );
}

function SimpleTable({ headers = [], rows = [] }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-2xl ring-1 ring-slate-200">
      <table className="min-w-full bg-white text-sm">
        <thead className="bg-slate-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-semibold text-slate-700">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-slate-100">
              {r.map((c, ci) => (
                <td key={ci} className="px-3 py-2 text-slate-700">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="mt-3 h-3 w-full animate-pulse rounded bg-slate-100" />
      <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-slate-100" />
    </div>
  );
}